import { z } from 'zod'
import { BRAND_ASSET_KEYS, FAQ_GROUPS, LINK_KEYS } from './catalogs'
import { PortalConfig, type SectionId } from './config'
import { applyEdit, deepEqual, type Actor, type Field } from './field'
import { Slug, accountId } from './ids'
import { ACCESS_STATUSES, PLATFORM_KEYS } from './platforms'
import { PromotionSchema, ScheduleSchema, emptySections, type SectionDocs } from './sections'

/**
 * Import format: the JSON that staff (or Claude Code) hand to the importer.
 * Leaves are plain strings, or {value, prefilled?, confirmed?} when flags matter.
 * Everything is optional; the importer merges into whatever exists.
 */
const Leaf = <T extends z.ZodTypeAny>(v: T) =>
  z.union([v, z.object({ value: v, prefilled: z.boolean().optional(), confirmed: z.boolean().optional() })])
const S = Leaf(z.string())

export const BrandInput = z.object({
  name: S, legal: S.optional(), industry: S.optional(), background: S.optional(), website: S.optional(),
  tone: S.optional(), themes: S.optional(), avoid: S.optional(),
  audience: z.object({ age: S, regulars: S, grow: S, occasions: S, price: S, neighborhoods: S }).partial().optional(),
  links: z.object(Object.fromEntries(LINK_KEYS.map((l) => [l.key, S.optional()]))).optional(),
  brandKit: z.object(Object.fromEntries(BRAND_ASSET_KEYS.map((a) => [a.key, z.object({ link: S.optional(), notes: S.optional() })]))).partial().optional(),
  /** This brand's FAQ answers by question id. `locations` (location id → answer) marks one that differs by location. */
  faq: z
    .record(z.string(), z.object({ answer: S.optional(), locations: z.record(Slug, S).optional(), /** Contact ids (keys of `contacts`) who handle it. */ contacts: z.array(z.string()).optional() }))
    .optional(),
})

export const LocationInput = z.object({
  brandId: Slug,
  name: S, address: S.optional(), phone: S.optional(), extra: S.optional(),
  /** Announced but not open yet; hours are then optional. Pair it with `opening`. */
  upcoming: z.boolean().optional(),
  opening: S.optional(),
  timezone: z.string().optional(),
  schedules: Leaf(z.array(ScheduleSchema)).optional(),
  /** Specials and promotions at this location (Taco Tuesday, a wine dinner). Opening hours go in `schedules`. */
  promotions: Leaf(z.array(PromotionSchema)).optional(),
})

export const AccountInput = z.object({
  brandId: Slug,
  platform: z.enum(PLATFORM_KEYS),
  handle: S.optional(),
  status: Leaf(z.enum(ACCESS_STATUSES.map((s) => s.value) as [string, ...string[]])).optional(),
  login: S.optional(),
  /** Routed to KMS by the importer; never written to a section doc. */
  password: z.string().optional(),
  notes: S.optional(),
})

export const ClientImport = z.object({
  client: z.object({ id: Slug, name: z.string().min(1) }),
  portalConfig: PortalConfig.partial().optional(),
  members: z.array(z.object({ email: z.string().email(), name: z.string().optional(), /** Their job at the client, e.g. "Director of Marketing". */ title: z.string().optional(), role: z.enum(['owner', 'collaborator']).default('collaborator') })).optional(),
  brands: z.record(Slug, BrandInput).optional(),
  locations: z.record(Slug, LocationInput).optional(),
  accounts: z.array(AccountInput).optional(),
  drives: z.record(z.string(), z.object({ label: S.optional(), url: S, access: S.optional() })).optional(),
  events: S.optional(),
  /** Questions asked only of this client; ids should start with "custom-". Answer them under `brands.*.faq`. */
  customQuestions: z.record(z.string(), z.object({ label: z.string().min(1), group: z.enum(FAQ_GROUPS) })).optional(),
  /** Standard question id → wording for this client only. */
  questionWording: z.record(z.string(), z.string().min(1)).optional(),
  /** Standard question id → group it's listed under for this client only. */
  questionGroups: z.record(z.string(), z.enum(FAQ_GROUPS)).optional(),
  /** `answer` here applies to every brand that has no answer of its own; `contact`/`email` are older free-text routing. */
  faq: z.record(z.string(), z.object({ answer: S.optional(), contact: S.optional(), email: S.optional() })).optional(),
  /** People and teams to route guests to; `topics` are CONTACT_TOPICS ids, `locations` empty = every location. */
  contacts: z.record(Slug, z.object({ name: S.optional(), role: S.optional(), phone: S.optional(), email: S.optional(), topics: z.array(z.string()).optional(), locations: z.array(Slug).optional() })).optional(),
  /** The team's kickoff notes for the whole client. Kept off the client's portal. */
  notes: S.optional(),
})
export type ClientImport = z.infer<typeof ClientImport>

type LeafIn<T> = T | { value: T; prefilled?: boolean; confirmed?: boolean }

export type ImportOptions = {
  runId: string
  /** 'replace' also removes brands/locations/accounts absent from the payload. */
  mode: 'merge' | 'replace'
  /** Overwrite fields the client has edited. Default false: client edits win. */
  overwriteClientEdits?: boolean
  now?: number
}

export type FieldWrite = { section: SectionId; path: string; value: unknown }
export type ReportRow = { section: SectionId; path: string; action: 'set' | 'unchanged' | 'kept_client_value' | 'deleted' | 'secret' }
export type ImportPlan = {
  writes: FieldWrite[]
  deletes: FieldWrite[]
  secrets: Array<{ accountId: string; login: string; password: string }>
  report: ReportRow[]
  summary: Record<ReportRow['action'], number>
}

function getPath(obj: unknown, path: string[]): unknown {
  let cur: unknown = obj
  for (const k of path) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[k]
  }
  return cur
}

/**
 * Pure planner: compares the payload to existing section docs and emits dotted-path writes.
 * Applying the plan is the caller's job (Admin SDK in the CLI/callable, so it is testable here).
 */
export function planImport(existing: Partial<SectionDocs>, payload: ClientImport, opts: ImportOptions): ImportPlan {
  const now = opts.now ?? Date.now()
  const actor: Actor = { kind: 'import', id: opts.runId }
  const docs: SectionDocs = { ...emptySections(), ...existing }
  const plan: ImportPlan = { writes: [], deletes: [], secrets: [], report: [], summary: { set: 0, unchanged: 0, kept_client_value: 0, deleted: 0, secret: 0 } }

  const note = (row: ReportRow) => {
    plan.report.push(row)
    plan.summary[row.action]++
  }

  function leaf<T>(section: SectionId, path: string, input: LeafIn<T> | undefined) {
    if (input === undefined) return
    const isObj = typeof input === 'object' && input !== null && 'value' in (input as object)
    const value = (isObj ? (input as { value: T }).value : input) as T
    const flags = isObj ? (input as { prefilled?: boolean; confirmed?: boolean }) : {}
    const cur = getPath(docs, [section, ...path.split('.')]) as Field<T> | undefined
    if (cur?.source === 'client' && !opts.overwriteClientEdits && !deepEqual(cur.value, value)) {
      note({ section, path, action: 'kept_client_value' })
      return
    }
    let next = applyEdit(cur, value, actor, now)
    if (next && flags.prefilled === false) next = { ...next, prefilled: false }
    if (next && flags.confirmed !== undefined) next = { ...next, confirmed: flags.confirmed }
    if (!next) {
      note({ section, path, action: 'unchanged' })
      return
    }
    plan.writes.push({ section, path, value: next })
    note({ section, path, action: 'set' })
  }

  function structural(section: SectionId, path: string, value: unknown) {
    const cur = getPath(docs, [section, ...path.split('.')])
    if (deepEqual(cur, value)) return
    plan.writes.push({ section, path, value })
  }

  /* Brands */
  const brandIds = Object.keys(payload.brands ?? {})
  if (brandIds.length) {
    const order = [...docs.brands.order]
    for (const id of brandIds) if (!order.includes(id)) order.push(id)
    const finalOrder = opts.mode === 'replace' ? order.filter((id) => brandIds.includes(id)) : order
    structural('brands', 'order', finalOrder)
    for (const [id, b] of Object.entries(payload.brands ?? {})) {
      if (!docs.brands.items[id]) structural('brands', `items.${id}.createdAt`, now)
      for (const k of ['name', 'legal', 'industry', 'background', 'website', 'tone', 'themes', 'avoid'] as const) leaf('brands', `items.${id}.${k}`, b[k])
      if (b.audience) for (const [k, v] of Object.entries(b.audience)) leaf('audience', `items.${id}.${k}`, v)
      if (b.links) for (const [k, v] of Object.entries(b.links)) leaf('links', `keyLinks.${id}.${k}`, v as LeafIn<string> | undefined)
      if (b.brandKit) for (const [ak, row] of Object.entries(b.brandKit)) { leaf('assets', `brandKit.${id}.${ak}.link`, row?.link); leaf('assets', `brandKit.${id}.${ak}.notes`, row?.notes) }
      for (const [qid, a] of Object.entries(b.faq ?? {})) {
        leaf('faq', `brands.${id}.${qid}.answer`, a.answer)
        if (a.contacts) structural('faq', `brands.${id}.${qid}.contacts`, a.contacts)
        if (a.locations) {
          structural('faq', `brands.${id}.${qid}.split`, true)
          for (const [lid, v] of Object.entries(a.locations)) leaf('faq', `brands.${id}.${qid}.locations.${lid}`, v)
        }
      }
    }
    if (opts.mode === 'replace') {
      for (const id of docs.brands.order) if (!brandIds.includes(id)) { plan.deletes.push({ section: 'brands', path: `items.${id}`, value: null }); note({ section: 'brands', path: `items.${id}`, action: 'deleted' }) }
    }
  }

  /* Locations */
  const locIds = Object.keys(payload.locations ?? {})
  if (locIds.length) {
    const order = [...docs.locations.order]
    for (const id of locIds) if (!order.includes(id)) order.push(id)
    structural('locations', 'order', opts.mode === 'replace' ? order.filter((id) => locIds.includes(id)) : order)
    for (const [id, l] of Object.entries(payload.locations ?? {})) {
      if (!docs.locations.items[id]) structural('locations', `items.${id}.createdAt`, now)
      structural('locations', `items.${id}.brandId`, l.brandId)
      structural('locations', `items.${id}.timezone`, l.timezone ?? docs.locations.items[id]?.timezone ?? 'America/New_York')
      if (l.upcoming !== undefined) structural('locations', `items.${id}.upcoming`, l.upcoming)
      for (const k of ['name', 'address', 'phone', 'extra', 'opening'] as const) leaf('locations', `items.${id}.${k}`, l[k])
      leaf('locations', `items.${id}.schedules`, l.schedules)
      leaf('specials', `promotions.${id}`, l.promotions)
    }
    if (opts.mode === 'replace') {
      for (const id of docs.locations.order) if (!locIds.includes(id)) { plan.deletes.push({ section: 'locations', path: `items.${id}`, value: null }); note({ section: 'locations', path: `items.${id}`, action: 'deleted' }) }
    }
  }

  /* Accounts */
  for (const a of payload.accounts ?? []) {
    const id = accountId(a.brandId, a.platform)
    structural('social', `items.${id}.brandId`, a.brandId)
    structural('social', `items.${id}.platform`, a.platform)
    if (!docs.social.items[id]) structural('social', `items.${id}.secretRef`, null)
    leaf('social', `items.${id}.handle`, a.handle)
    leaf('social', `items.${id}.status`, a.status)
    leaf('social', `items.${id}.login`, a.login)
    leaf('social', `items.${id}.notes`, a.notes)
    if (a.password) {
      const login = typeof a.login === 'string' ? a.login : a.login?.value ?? ''
      plan.secrets.push({ accountId: id, login, password: a.password })
      note({ section: 'social', path: `items.${id}.secretRef`, action: 'secret' })
    }
  }

  /* Drives, events, FAQ, contacts, kickoff notes */
  for (const [id, d] of Object.entries(payload.drives ?? {})) { leaf('assets', `drives.${id}.label`, d.label); leaf('assets', `drives.${id}.url`, d.url); leaf('assets', `drives.${id}.access`, d.access) }
  leaf('specials', 'events', payload.events)
  Object.entries(payload.customQuestions ?? {}).forEach(([qid, q], i) => {
    if (!docs.faq.custom?.[qid]) structural('faq', `custom.${qid}.createdAt`, now)
    structural('faq', `custom.${qid}.label`, q.label)
    structural('faq', `custom.${qid}.group`, q.group)
    structural('faq', `custom.${qid}.order`, 1000 + i)
  })
  for (const [qid, label] of Object.entries(payload.questionWording ?? {})) structural('faq', `labels.${qid}`, label)
  for (const [qid, group] of Object.entries(payload.questionGroups ?? {})) structural('faq', `groups.${qid}`, group)
  const allBrands = [...new Set([...docs.brands.order, ...brandIds])]
  for (const [qid, a] of Object.entries(payload.faq ?? {})) {
    for (const bid of allBrands) if (!payload.brands?.[bid]?.faq?.[qid]) leaf('faq', `brands.${bid}.${qid}.answer`, a.answer)
    leaf('faq', `answers.${qid}.contact`, a.contact)
    leaf('faq', `answers.${qid}.email`, a.email)
  }
  for (const [cid, c] of Object.entries(payload.contacts ?? {})) {
    if (!docs.contacts.items?.[cid]) structural('contacts', `items.${cid}.createdAt`, now)
    if (c.topics) structural('contacts', `items.${cid}.topics`, c.topics)
    if (c.locations) structural('contacts', `items.${cid}.locationIds`, c.locations)
    for (const k of ['name', 'role', 'phone', 'email'] as const) leaf('contacts', `items.${cid}.${k}`, c[k])
  }
  leaf('brands', 'notes', payload.notes)

  return plan
}

/** Apply a plan's writes to in-memory docs (used by tests and by the emulator seed). */
export function applyPlanLocally(existing: Partial<SectionDocs>, plan: ImportPlan): SectionDocs {
  const docs = JSON.parse(JSON.stringify({ ...emptySections(), ...existing })) as SectionDocs
  const setPath = (obj: Record<string, unknown>, path: string[], value: unknown) => {
    let cur = obj
    for (const k of path.slice(0, -1)) {
      if (typeof cur[k] !== 'object' || cur[k] === null) cur[k] = {}
      cur = cur[k] as Record<string, unknown>
    }
    cur[path[path.length - 1]!] = value
  }
  const delPath = (obj: Record<string, unknown>, path: string[]) => {
    let cur = obj
    for (const k of path.slice(0, -1)) {
      if (typeof cur[k] !== 'object' || cur[k] === null) return
      cur = cur[k] as Record<string, unknown>
    }
    delete cur[path[path.length - 1]!]
  }
  for (const w of plan.writes) setPath(docs[w.section] as unknown as Record<string, unknown>, w.path.split('.'), w.value)
  for (const d of plan.deletes) delPath(docs[d.section] as unknown as Record<string, unknown>, d.path.split('.'))
  return docs
}
